import React, { useEffect, useRef, useState } from "react";
import styled from "@emotion/styled";
import DisplayType from "utils/displayType";
import { RegexpPhone, RemoveHypen } from "utils/commonUtil";
import { useLanguage } from "i18n/LanguageContext";

interface Props {
  className?: string;
  label?: string;
  defaultValue?: string;
  placeHolder?: string;
  isError?: boolean;
  errorMessage?: string;
  maxLength?: number;
  inputType: "text" | "phone" | "email";
  onFocus?: () => void;
  onChangeValue?: (value: string) => void;
}
const ContactInput: React.FC<Props> = (props) => {
  const mode = DisplayType();
  const { t } = useLanguage();
  const inputRef = useRef<HTMLInputElement>(null);
  const [value, updateValue] = useState<string>("");

  const onChangeValue = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (props.inputType === "phone") {
      e.target.value = e.target.value
        .replace(/[^0-9.]/g, "")
        .replace(/(\..*)\./g, "$1");
    }
    updateValue(e.target.value);
  };

  const onFocus = (e: React.FocusEvent<HTMLInputElement, Element>) => {
    if (inputRef.current === null) return;

    if (props.inputType === "phone") {
      inputRef.current.maxLength = 11;
      updateValue(RemoveHypen(value));
    }

    if (props.onFocus) props.onFocus();
  };
  const onBlur = (e: React.FocusEvent<HTMLInputElement, Element>) => {
    if (inputRef.current === null) return;

    if (props.inputType === "phone") {
      inputRef.current.maxLength = 13;
      inputRef.current.value = RegexpPhone(value);
      updateValue(RegexpPhone(value));
    }

    if (props.onChangeValue) {
      props.onChangeValue(e.target.value);
    }
  };

  useEffect(() => {
    if (props.defaultValue !== undefined) {
      updateValue(props.defaultValue);
    }
  }, [props.defaultValue]);

  return (
    <Container className={props.className}>
      {props.label && (
        <div
          className={`${
            mode === "Mobile" ? "font-sub-title4" : "font-sub-title4"
          }`}
          style={{ color: "var(--grey-50)" }}
        >
          {props.label}
        </div>
      )}
      <input
        className={`${mode === "Mobile" ? "font-body3" : "font-body3"} ${
          props.isError && "input_error"
        }`}
        ref={inputRef}
        type={"text"}
        placeholder={props.placeHolder || t("common", "input.placeholder")}
        value={value}
        maxLength={props.maxLength}
        onChange={onChangeValue}
        onFocus={onFocus}
        onBlur={onBlur}
      />
      {props.isError && (
        <div
          className={`error__msg ${
            mode === "Mobile" ? "font-body4" : "font-body4"
          }`}
        >
          {props.errorMessage || t("common", "input.invalidValue")}
        </div>
      )}
    </Container>
  );
};

const Container = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;

  input {
    width: 100%;
    height: 48px;
    min-height: 48px;
    max-height: 48px;
    background-color: var(--bg-03);
    border: 1px solid var(--grey-00);
    border-radius: 4px;
    padding-left: 16px;
    color: var(--grey-60);

    transition: all 0.1s ease;
    :invalid {
      border: 1px solid #e02020;
      background-color: #fdeded;
      animation: shake 300ms;
      :focus {
        outline: none;
      }
    }

    :focus {
      outline: 1px solid var(--primary);
    }

    :disabled {
      background-color: var(--grey-00);
      color: var(--grey-20);
    }

    ::placeholder {
      color: var(--grey-30);
    }
  }

  .input_error {
    border: 1px solid #e02020;
    background-color: #fdeded;
    animation: shake 300ms;
    :focus {
      outline: none;
    }
  }

  .error__msg {
    position: absolute;
    left: 10px;
    bottom: -24px;
    color: #e02020;
    :focus {
      outline: none;
    }
  }
`;

export default React.memo(ContactInput);
